import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  UploadFile as UploadFileIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Groups as PeopleIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  ToggleOn as ToggleOnIcon,
  Face as FaceIcon,
} from '@mui/icons-material';
import ListFilterBar from '../../common/ListFilterBar';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import StatCards from '../../common/StatCards';
import TableList, { TableRowActions } from '../../common/TableList';
import { getNavIconForPath } from '../../../utils/navIcons';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import { USER_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { createBulkHandlers } from '../../../utils/listBulkSelection';
import FeedbackMessage from '../../common/FeedbackMessage';
import FaceRecognitionStatusChip from '../../common/FaceRecognitionStatusChip';
import { hasFaceRegistered } from '../../../utils/userFaceRecognition';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import StorageAvatar from '../../common/StorageAvatar';
import { canManageUsers as canManageUsersAccess, canEditUser as canEditUserAccess, canDeleteUser as canDeleteUserAccess, canCreateUser as canCreateUserAccess } from '../../../utils/userPermissions';

// ─── Design tokens — cyberpunk dark system ────────────────────────────────────
const B = {
  600: '#2563EB',
  700: '#1D4ED8',
  900: '#1E3A8A',
};

const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';

const UserList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const fileInputRef = useRef(null);

  // Update permission checks
  const canManageUsers = canManageUsersAccess(currentUser);
  const canEditUser = (user) => canEditUserAccess(currentUser, user?.id);
  const canDeleteUser = (user) => canDeleteUserAccess(currentUser, user?.id);
  const canCreateUser = canCreateUserAccess(currentUser);

  const filterOptions = [
    {
      name: 'role',
      label: 'Role',
      type: 'select',
      options: [
        { value: 'admin', label: 'Admin' },
        { value: 'user', label: 'User' }
      ]
    },
    {
      name: 'bureau',
      label: 'Bureau',
      type: 'select',
      options: Array.from(new Map(users
        .filter(u => u.bureau?.id && u.bureau?.name)
        .map(u => [u.bureau.id, { value: String(u.bureau.id), label: u.bureau.name }]))
        .values())
    },
    {
      name: 'department',
      label: 'Department',
      type: 'select',
      options: [
        { value: 'programming', label: 'Programming' },
        { value: 'electronic', label: 'Electronic' },
        { value: 'mechanism', label: 'Mechanism' }
      ]
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ]
    }
  ];

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    fetchUsers();
  }, [currentUser]);

  const fetchUsers = async ({ forceRefresh = false } = {}) => {
    try {
      setLoading(true);
      let attempt = 0;
      let lastError = null;
      while (attempt < 2) {
        try {
          const response = await api.get('/users', { cache: false });
          const processedUsers = response.data.map(user => ({
            ...user,
            is_admin: Boolean(user.is_admin),
            status: (user.status || 'active') === 'inactive' ? 'inactive' : 'active',
          }));
          setUsers(processedUsers);
          setError(null);
          return;
        } catch (e) {
          lastError = e;
          if (e.response?.status === 401) {
            break;
          }
          await new Promise(r => setTimeout(r, 400));
          attempt++;
        }
      }
      console.error('Error fetching users:', lastError);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (user) => {
    if (user.id === currentUser.id) {
      setError('You cannot delete your own account');
      return;
    }
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const userName = selectedUser.name;
      await api.delete(`/users/${selectedUser.id}`);
      setUsers(users.filter(u => u.id !== selectedUser.id));
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      setSuccess(`User "${userName}" has been deleted successfully!`);
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user. Please try again.');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({});
    setActiveStatFilter('all');
    setSearchTerm('');
    setPage(0);
  };

  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'admin', 'user', 'active'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);
    const nextFilters = { ...filters };
    if (nextFilter === 'all') {
      delete nextFilters.role;
      delete nextFilters.status;
    } else {
      if (nextFilter === 'active') {
        nextFilters.status = 'active';
        delete nextFilters.role;
      } else {
        nextFilters.role = nextFilter;
        delete nextFilters.status;
      }
    }
    setFilters(nextFilters);
    setPage(0);
  };

  const parseCsvContent = (content) => {
    const rows = [];
    let current = '';
    let row = [];
    let inQuotes = false;

    for (let i = 0; i < content.length; i++) {
      const char = content[i];
      const nextChar = content[i + 1];

      if (char === '"' && inQuotes && nextChar === '"') {
        current += '"';
        i++;
        continue;
      }

      if (char === '"') {
        inQuotes = !inQuotes;
        continue;
      }

      if (char === ',' && !inQuotes) {
        row.push(current.trim());
        current = '';
        continue;
      }

      if ((char === '\n' || char === '\r') && !inQuotes) {
        if (char === '\r' && nextChar === '\n') {
          i++;
        }
        row.push(current.trim());
        if (row.some((cell) => cell !== '')) {
          rows.push(row);
        }
        row = [];
        current = '';
        continue;
      }

      current += char;
    }

    if (current !== '' || row.length > 0) {
      row.push(current.trim());
      if (row.some((cell) => cell !== '')) {
        rows.push(row);
      }
    }

    return rows;
  };

  const normalizeHeader = (header = '') =>
    header.toString().trim().toLowerCase().replace(/[\s-]+/g, '_');

  const toNullableString = (value) => {
    if (value === null || value === undefined) return null;
    const trimmed = String(value).trim();
    return trimmed ? trimmed : null;
  };

  const getCellByHeaders = (rowObj, headers) => {
    for (const header of headers) {
      const key = normalizeHeader(header);
      if (Object.prototype.hasOwnProperty.call(rowObj, key)) {
        return rowObj[key];
      }
    }
    return undefined;
  };

  const resolveIdFromValue = (value, lookupMap) => {
    if (value === undefined || value === null || value === '') return null;
    const raw = String(value).trim();
    if (!raw) return null;
    if (/^\d+$/.test(raw)) return Number(raw);
    const key = raw.toLowerCase();
    return lookupMap.get(key) ?? null;
  };

  const buildLookupMap = (items) => {
    const map = new Map();
    (items || []).forEach((item) => {
      if (!item) return;
      const name = String(item.name || '').trim().toLowerCase();
      if (name) map.set(name, item.id);
    });
    return map;
  };

  const handleDownloadTemplate = () => {
    const templateHeaders = [
      'name',
      'email',
      'password',
      'course',
      'matric_number',
      'ic_number',
      'passport_number',
      'bureau',
      'department',
      'management'
    ];

    const templateRows = [
      [
        'John Doe',
        'john.doe@example.com',
        'Password123',
        'Bachelor of Engineering',
        'A22EC0001',
        '010101-01-0101',
        '',
        'Partnership',
        'Programming',
        'Team Leader'
      ],
      [
        'Admin User',
        'admin.user@example.com',
        'Password123',
        '',
        'A22EC0002',
        '',
        '',
        'Multimedia',
        'Electronic',
        'Secretary'
      ]
    ];

    const escapeCell = (value) => {
      const stringValue = String(value ?? '');
      if (/[",\n]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const csvContent = [templateHeaders, ...templateRows]
      .map((row) => row.map(escapeCell).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'user_import_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportUsersClick = () => {
    if (importLoading) return;
    fileInputRef.current?.click();
  };

  const handleImportUsersFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setImportLoading(true);
      setError(null);
      setSuccess(null);

      const extension = file.name.toLowerCase().split('.').pop();
      let matrix = [];

      if (extension === 'csv') {
        const text = await file.text();
        matrix = parseCsvContent(text);
      } else if (extension === 'xlsx' || extension === 'xls') {
        const xlsx = await import('xlsx');
        const data = await file.arrayBuffer();
        const workbook = xlsx.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) {
          throw new Error('The uploaded Excel file has no sheets.');
        }
        const sheet = workbook.Sheets[firstSheetName];
        matrix = xlsx.utils.sheet_to_json(sheet, {
          header: 1,
          raw: false,
          defval: ''
        });
      } else {
        throw new Error('Unsupported file format. Please use CSV, XLSX, or XLS.');
      }

      if (!matrix.length || matrix.length < 2) {
        throw new Error('File is empty or missing data rows.');
      }

      const [rawHeaders, ...rawRows] = matrix;
      const headers = rawHeaders.map((header) => normalizeHeader(header));
      const rowObjects = rawRows
        .filter((row) => row.some((cell) => String(cell || '').trim() !== ''))
        .map((row) => {
          const obj = {};
          headers.forEach((header, idx) => {
            obj[header] = row[idx];
          });
          return obj;
        });

      if (!rowObjects.length) {
        throw new Error('No valid data rows found in the uploaded file.');
      }

      const [bureausRes, departmentsRes, managementRes] = await Promise.all([
        api.get('/bureaus', { cache: false }),
        api.get('/departments', { cache: false }),
        api.get('/managements', { cache: false })
      ]);

      const bureauMap = buildLookupMap(bureausRes.data);
      const departmentMap = buildLookupMap(departmentsRes.data);
      const managementMap = buildLookupMap(managementRes.data);

      const errors = [];
      let createdCount = 0;

      for (let index = 0; index < rowObjects.length; index++) {
        const rowObj = rowObjects[index];
        const rowNumber = index + 2; // +2 because row 1 is header

        const name = toNullableString(getCellByHeaders(rowObj, ['name', 'full_name']));
        const email = toNullableString(getCellByHeaders(rowObj, ['email', 'email_address']));
        const password = toNullableString(getCellByHeaders(rowObj, ['password', 'pass']));

        if (!name || !email || !password) {
          errors.push(`Row ${rowNumber}: name, email, and password are required.`);
          continue;
        }

        const bureauInput = getCellByHeaders(rowObj, ['bureau_id', 'bureau', 'bureau_name']);
        const departmentInput = getCellByHeaders(rowObj, ['department_id', 'department', 'department_name']);
        const managementInput = getCellByHeaders(rowObj, ['management_id', 'management', 'management_name']);

        const bureauId = resolveIdFromValue(bureauInput, bureauMap);
        const departmentId = resolveIdFromValue(departmentInput, departmentMap);
        const managementId = resolveIdFromValue(managementInput, managementMap);

        if (toNullableString(bureauInput) && bureauId === null) {
          errors.push(`Row ${rowNumber}: bureau "${bureauInput}" not found.`);
          continue;
        }
        if (toNullableString(departmentInput) && departmentId === null) {
          errors.push(`Row ${rowNumber}: department "${departmentInput}" not found.`);
          continue;
        }
        if (toNullableString(managementInput) && managementId === null) {
          errors.push(`Row ${rowNumber}: management "${managementInput}" not found.`);
          continue;
        }

        const payload = {
          name,
          email,
          password,
          course: toNullableString(getCellByHeaders(rowObj, ['course'])),
          matric_number: toNullableString(getCellByHeaders(rowObj, ['matric_number', 'matric', 'student_id'])),
          ic_number: toNullableString(getCellByHeaders(rowObj, ['ic_number', 'ic', 'ic_no'])),
          passport_number: toNullableString(getCellByHeaders(rowObj, ['passport_number', 'passport', 'passport_no'])),
          bureau_id: bureauId,
          department_id: departmentId,
          management_id: managementId
        };

        try {
          await api.post('/users', payload);
          createdCount++;
        } catch (createError) {
          const backendMessage = createError.response?.data?.message;
          const validationErrors = createError.response?.data?.errors;
          const firstValidationError = validationErrors
            ? Object.values(validationErrors).flat()?.[0]
            : null;
          const message = firstValidationError || backendMessage || createError.message || 'Failed to create user.';
          errors.push(`Row ${rowNumber}: ${message}`);
        }
      }

      await fetchUsers();

      if (createdCount > 0 && errors.length === 0) {
        setSuccess(`Successfully imported ${createdCount} user(s).`);
      } else if (createdCount > 0 && errors.length > 0) {
        setSuccess(`Imported ${createdCount} user(s), but ${errors.length} row(s) failed.`);
        setError(errors.slice(0, 6).join(' '));
      } else {
        setError(`No users were imported. ${errors.slice(0, 6).join(' ')}`);
      }
    } catch (importError) {
      setError(importError.message || 'Failed to import users from file.');
    } finally {
      setImportLoading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.matric_number && user.matric_number.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilters = Object.entries(filters).every(([key, value]) => {
      if (key === 'role') {
        if (value === 'admin') return user.is_admin;
        if (value === 'user') return !user.is_admin;
      }
      if (key === 'bureau' && value) {
        return String(user.bureau_id || user.bureau?.id) === String(value);
      }
      if (key === 'department') {
        if (value === 'programming') return user.department_id === 1;
        if (value === 'electronic') return user.department_id === 2;
        if (value === 'mechanism') return user.department_id === 3;
      }
      if (key === 'status' && value) {
        return (user.status || 'active') === value;
      }
      return true;
    });

    return matchesSearch && matchesFilters;
  });

  const statusSortRank = (user) => ((user.status || 'active') === 'inactive' ? 1 : 0);
  const createdAtMs = (user) => {
    const t = user.created_at ? new Date(user.created_at).getTime() : 0;
    return Number.isFinite(t) ? t : 0;
  };

  const sortedFilteredUsers = useMemo(() => (
    [...filteredUsers].sort((a, b) => {
      const byStatus = statusSortRank(a) - statusSortRank(b);
      if (byStatus !== 0) return byStatus;
      const byCreated = createdAtMs(b) - createdAtMs(a);
      if (byCreated !== 0) return byCreated;
      return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
    })
  ), [filteredUsers]);

  // Define stats for StatCards
  // ─── STATS CALCULATED FROM ALL DATA (NOT FILTERED) ───────────────────────────────
  const statsData = [
    {
      icon: PersonIcon,
      value: users.length,
      label: 'Total Users',
      color: 'info'
    },
    {
      icon: AdminIcon,
      value: users.filter(u => u.is_admin).length,
      label: 'Administrators',
      color: 'error'
    },
    {
      icon: BusinessIcon,
      value: users.filter(u => !u.is_admin).length,
      label: 'Regular Users',
      color: 'secondary'
    },
    {
      icon: ToggleOnIcon,
      value: users.filter(u => (u.status || 'active') === 'active').length,
      label: 'Active Users',
      color: 'success'
    }
  ];

  // Define columns for TableList
  const columns = [
    {
      key: 'user',
      sortKey: 'name',
      label: 'User',
      icon: PersonIcon,
      primary: true,
      minWidth: 220,
      render: (user) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <StorageAvatar
            storagePath={user.profile_picture || user.profile_picture_url}
            alt={user.name}
            sx={{ 
              width: 44, 
              height: 44,
              boxShadow: 2,
              border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
            }}
          >
            {user.name.charAt(0).toUpperCase()}
          </StorageAvatar>
          <Box>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 500,
                color: alpha(theme.palette.common.white, 0.95),
                '& a': {
                  textDecoration: 'none',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    textDecoration: 'underline',
                  }
                }
              }}
            >
              {user.name}
            </Typography>
            <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.72) }}>
              {user.email}
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      key: 'bureau.name',
      label: 'Bureau',
      icon: BusinessIcon,
      sortable: true,
      tone: 'secondary',
      minWidth: 150,
      render: (user) => (
        <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.9) }}>
          {user.bureau?.name || (
            <span style={{ color: theme.palette.text.disabled }}>-</span>
          )}
        </Typography>
      )
    },
    {
      key: 'department.name',
      label: 'Department',
      icon: CategoryIcon,
      sortable: true,
      tone: 'secondary',
      minWidth: 160,
      render: (user) => (
        <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.9) }}>
          {user.department?.name || (
            <span style={{ color: theme.palette.text.disabled }}>-</span>
          )}
        </Typography>
      )
    },
    {
      key: 'role',
      sortKey: 'is_admin',
      label: 'Role',
      align: 'center',
      tone: 'badge',
      minWidth: 165,
      render: (user) => (
        user.is_admin ? (
          <Chip
            icon={<AdminIcon />}
            label="Administrator"
            color="error"
            size="small"
            sx={{ 
              fontWeight: 500,
              boxShadow: 1,
              width: 'fit-content',
              '& .MuiChip-label': {
                overflow: 'visible',
                textOverflow: 'clip',
                whiteSpace: 'nowrap',
              },
            }}
          />
        ) : (
          <Chip
            icon={PersonIcon}
            label="User"
            color="primary"
            size="small"
            variant="outlined"
            sx={{ 
              fontWeight: 500,
            }}
          />
        )
      )
    },
    {
      key: 'status',
      sortKey: 'status',
      sortAccessor: (user) => statusSortRank(user),
      label: 'Status',
      align: 'center',
      tone: 'badge',
      minWidth: 170,
      render: (user) => {
        const isInactive = (user.status || 'active') === 'inactive';
        return (
          <Chip
            label={isInactive ? 'Inactive' : 'Active'}
            color={isInactive ? 'warning' : 'success'}
            size="small"
            variant={isInactive ? 'outlined' : 'filled'}
            sx={{ fontWeight: 500 }}
          />
        );
      }
    },
    {
      key: 'face_recognition',
      sortKey: 'face_registered_at',
      sortAccessor: (user) => (hasFaceRegistered(user) ? 1 : 0),
      label: 'Face',
      icon: FaceIcon,
      align: 'center',
      tone: 'badge',
      minWidth: 150,
      render: (user) => <FaceRecognitionStatusChip user={user} />,
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      tone: 'action',
      minWidth: 120,
      render: (user) => (
        <TableRowActions
          viewTitle="View User"
          editTitle="Edit User"
          deleteTitle="Delete User"
          onView={(e) => { e.stopPropagation(); navigate(`/users/details/${user.id}`); }}
          onEdit={canEditUser(user) ? (e) => { e.stopPropagation(); navigate(`/users/${user.id}/edit`); } : undefined}
          onDelete={canDeleteUser(user) ? (e) => { e.stopPropagation(); handleDelete(user); } : undefined}
          showEdit={canManageUsers || canEditUser(user)}
          showDelete={canDeleteUser(user)}
        />
      )
    }
  ];

  if (!currentUser) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <FeedbackMessage
          message="Please log in to view this page."
          severity="error"
          sx={{
            fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem',
            background: alpha('#450a0a', 0.9), color: '#fca5a5', border: '1px solid rgba(248,113,113,0.3)'
          }}
        />
      </Container>
    );
  }

  if (loading) {
    return (  
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading message="Loading users list" size={56} variant="primary" />
    </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 1, sm: 2, md: 4 }, mb: { xs: 3, md: 4 }, px: { xs: 0, sm: 2 } }}>
      {/* Header Section */}
      <Header 
        title="User" 
        subtitle="Manage system users and their permissions"
        icon={getNavIconForPath('/users')}
        actionButton={canCreateUser ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: { xs: 'stretch', sm: 'flex-end' },
              width: '100%',
            }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => navigate('/users/add')}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.22)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.34)',
                },
                borderRadius: 2,
                px: 2.5,
                py: 1.5,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Add User
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<UploadFileIcon />}
              onClick={handleImportUsersClick}
              disabled={importLoading}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.16)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.28)',
                },
                borderRadius: 2,
                px: 2.5,
                py: 1.5,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              {importLoading ? 'Importing...' : 'Import CSV/Excel'}
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.16)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.28)',
                },
                borderRadius: 2,
                px: 2.5,
                py: 1.5,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Download Template
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleImportUsersFile}
            />
          </Box>
        ) : null}
      />

      {/* Error Message */}
      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
          color: '#fca5a5',
          mb: 3,
        }}
      />

      {/* Success Message */}
      <FeedbackMessage
        message={success}
        severity="success"
        onClose={() => setSuccess(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: `linear-gradient(135deg, ${alpha(NEON3, 0.1)}, ${alpha(SURFACE_MID, 0.95)})`,
          border: `1px solid ${alpha(NEON3, 0.35)}`,
          boxShadow: `0 4px 20px ${alpha(NEON3, 0.15)}`,
          color: alpha(NEON3, 0.95),
          mb: 2,
        }}
      />
      
      {/* Stats Cards */}
      <StatCards
        stats={statsData}
        onCardClick={handleStatCardClick}
        activeIndex={
          activeStatFilter === 'admin'
            ? 1
            : activeStatFilter === 'user'
              ? 2
              : activeStatFilter === 'active'
                ? 3
                : 0
        }
      />

      <ListFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        filterOptions={filterOptions}
        searchPlaceholder="Search by name, email, or matric number..."
      />

      <ListBulkToolbar
        config={USER_BULK_CONFIG}
        currentUser={currentUser}
        selectedIds={selectedIds}
        onClearSelection={() => setSelectedIds([])}
        {...createBulkHandlers({
          endpoint: USER_BULK_CONFIG.endpoint,
          setItems: setUsers,
          selectedIds,
          clearSelection: () => setSelectedIds([]),
          setSuccess,
          setError,
          successLabel: 'user(s)',
          refetch: fetchUsers,
          filterDeleteIds: (ids) =>
            ids.filter((id) => String(id) !== String(currentUser?.id)),
        })}
      />

      {/* Users Table */}
      <TableList
        columns={columns}
        data={sortedFilteredUsers}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        loading={loading}
        selectable={USER_BULK_CONFIG.canBulk(currentUser)}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        getRowId={(user) => user.id}
        emptyState={{
          icon: <PersonIcon />,
          title: 'No users found',
          description: searchTerm || Object.keys(filters).length > 0
            ? "Try adjusting your filters to see more users."
            : "Get started by adding your first user."
        }}
        onRowClick={(user) => navigate(`/users/details/${user.id}`)}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={confirmDelete}
        itemType="user"
        itemName={selectedUser?.name}
        description="This action cannot be undone and will permanently remove all user data."
        confirmLabel="Delete User"
      />
    </Container>
  );
};

export default UserList;
